/**
 * The six landing drawings.
 *
 * ONE HAND, THREE MARKS. Every drawing is built from a sheet, a ruled line and a thin connector, so
 * the set reads as one voice rather than six clip-arts — and each one says the thing its own band
 * already says in words.
 *
 * DECORATIVE, WITHOUT EXCEPTION. Every drawing is `aria-hidden` and `focusable="false"`, and none
 * contains a `<text>` node: SVG text would need translating like any other copy, and shapes badly in
 * an RTL page for no gain. Nothing here carries information that is not also in the prose beside it.
 *
 * COLOUR COMES FROM `currentColor`, never from a fill on the node. That is what lets landing.css
 * reassign the whole set for the deep ground with a single declaration, and it is why a drawing can
 * be moved between bands without editing it. The only exceptions are the four accent classes
 * (`ta-acc`, `ta-brass`, `ta-line`, `ta-amber`) and `ta-foam`, which name a specific colour on
 * purpose — one accent per drawing, no more.
 *
 * Sheets are FILLED rather than outlined: an overlapping stack only reads as depth if the sheet in
 * front occludes the one behind it.
 *
 * THE DRAWINGS ARE NOT STATIC ANY MORE, and one of them says so in its own markup. landing-reveal.ts
 * measures every stroke below and draws it in as the band arrives — which needs nothing from this
 * file, because a stroke is recognised by the `ta-s` / `ta-s2` / `ta-line` classes that were already
 * here. The single exception is the beer, whose liquid RISES rather than being drawn, and that does
 * need structure: a clip shaped like the glass, and a group to move inside it.
 */

interface ArtProps {
  /** Placement only — the drawing sizes itself to its container. */
  className?: string;
}

/**
 * The group that rises when the glass fills.
 *
 * Exported because landing-reveal.ts has to know NOT to ink the strokes inside it — the bubbles are
 * already moving, and drawing them at the same time is one effect too many. Declared here rather
 * than there so the drawing owns its own structure and the two cannot drift apart.
 */
export const POUR = 'tenders-art-pour';

/** The clip shaped like the glass interior, so the rising liquid never crosses the walls. */
const GLASS_CLIP = 'tenders-art-glass';

/** Five publications, five authorities, five formats, landing in one board. */
export function ManyIntoOne({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-many ${className ?? ''}`} viewBox="-8 -10 350 286" aria-hidden="true" focusable="false">
      {/* five publications, five authorities, five formats */}
      <g opacity=".82">
        <g transform="rotate(-11 258 42)"><rect className="ta-sheet" x="231" y="8" width="54" height="68" rx="4"/><path className="ta-s2" d="M240 25h33M240 36h36M240 47h24"/></g>
        <g transform="rotate(8 300 106)"><rect className="ta-sheet" x="273" y="72" width="54" height="68" rx="4"/><path className="ta-s2" d="M282 89h33M282 100h36M282 111h22"/></g>
        <g transform="rotate(-4 246 134)"><rect className="ta-sheet" x="219" y="100" width="54" height="68" rx="4"/><path className="ta-s2" d="M228 117h33M228 128h36M228 139h27"/></g>
        <g transform="rotate(13 292 198)"><rect className="ta-sheet" x="265" y="164" width="54" height="68" rx="4"/><path className="ta-s2" d="M274 181h33M274 192h36M274 203h20"/></g>
        <g transform="rotate(-7 232 220)"><rect className="ta-sheet" x="205" y="186" width="54" height="68" rx="4"/><path className="ta-s2" d="M214 203h33M214 214h36M214 225h29"/></g>
      </g>
      {/* and all five land in the same place */}
      <g className="ta-s2" opacity=".6">
        <path d="M228 44 Q190 58 158 84"/><path d="M270 108 Q212 108 158 112"/>
        <path d="M216 136 Q186 134 158 134"/><path d="M262 200 Q206 184 158 160"/>
        <path d="M202 222 Q178 206 158 186"/>
      </g>
      {/* the one board */}
      <rect className="ta-sheet" x="14" y="50" width="140" height="176" rx="7"/>
      <path className="ta-s" d="M14 80h140"/>
      <circle className="ta-s" cx="26" cy="65" r="2.6"/><path className="ta-s2" d="M40 65h44"/>
      <rect className="ta-acc" x="26" y="128" width="114" height="14" rx="3"/>
      <path className="ta-s2" d="M30 100h108M30 118h96M30 135h100M30 154h88M30 172h104M30 190h84M30 208h64"/>
    </svg>
  );
}

/** Six kinds of publisher, every one dropping into the same rail. The brass drop is a source added this month. */
export function SourceRail({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-rail ${className ?? ''}`} viewBox="0 -12 720 114" aria-hidden="true" focusable="false">
        <g className="ta-s">
          {/* city hall */}
          <path d="M45 30 L85 12 L125 30 Z"/><path d="M47 30h76M47 36h76"/>
          <path className="ta-s2" d="M60 36v18M76 36v18M92 36v18M108 36v18"/>
          <path d="M43 54h84M39 58h92"/>
          {/* a ministry */}
          <rect x="157" y="18" width="76" height="40" rx="2"/>
          <path className="ta-s2" d="M167 28h12M187 28h12M207 28h12M167 40h12M187 40h12M207 40h12"/>
          <path d="M189 58v-10h12v10M151 58h88"/>
          {/* a water utility */}
          <path d="M278 16 H332 L324 36 H286 Z"/>
          <path className="ta-s2" d="M300 36v22M310 36v22M300 43h10M300 50h10"/>
          <path d="M268 58h74"/>
          {/* a transport company */}
          <path d="M363 30h104M363 36h104"/>
          <path className="ta-s2" d="M375 58v-10a12 12 0 0 1 24 0v10M403 58v-10a12 12 0 0 1 24 0v10M431 58v-10a12 12 0 0 1 24 0v10"/>
          <path d="M359 58h112"/>
          {/* an authority */}
          <rect x="505" y="10" width="40" height="48" rx="2"/>
          <path className="ta-s2" d="M505 24h40M505 36h40M505 48h40"/>
          <path d="M525 10v-9"/><circle cx="525" cy="-3" r="2.4"/>
          <path d="M497 58h56"/>
          {/* a state-owned plant */}
          <rect x="600" y="26" width="24" height="32" rx="1"/><path d="M600 26 a12 8 0 0 1 24 0"/>
          <path d="M630 58v-20h34v20M628 38 L647 28 L666 38M654 28v-18h8v18"/>
          <path d="M594 58h78"/>
        </g>
        {/* each one drops into the same rail; the brass drop is this month's */}
        <g className="ta-s2"><path d="M85 58v26M195 58v26M415 58v26M525 58v26M635 58v26"/></g>
        <path className="ta-line" d="M305 58v26"/>
        <path className="ta-s" d="M26 84 H694M26 78v12M694 78v12"/>
        <g className="ta-s"><circle cx="85" cy="84" r="3"/><circle cx="195" cy="84" r="3"/><circle cx="415" cy="84" r="3"/><circle cx="525" cy="84" r="3"/><circle cx="635" cy="84" r="3"/></g>
        <circle className="ta-brass" cx="305" cy="84" r="4"/>
      </svg>
  );
}

/** The price, in the only unit that makes it legible. */
export function OneBeer({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-beer ${className ?? ''}`} viewBox="-5 -6 130 178" aria-hidden="true" focusable="false">
          {/* The liquid and its bubbles ride up together inside the glass, never past its walls —
              and the surface stays a curve the whole way, which a growing rectangle cannot do. */}
          <defs>
            <clipPath id={GLASS_CLIP}><path d="M18 40 L30 152 Q60 160 90 152 L102 40 Z"/></clipPath>
          </defs>
          <g clipPath={`url(#${GLASS_CLIP})`}>
            <g className={POUR}>
              <path className="ta-amber" d="M20.5 52 Q60 61 99.5 52 L89.5 150 Q60 158 30.5 150 Z"/>
              <g className="ta-s2" opacity=".75"><circle cx="38" cy="80" r="3.2"/><circle cx="72" cy="94" r="3.6"/><circle cx="52" cy="110" r="2.6"/><circle cx="44" cy="130" r="2.2"/><circle cx="78" cy="132" r="2.8"/></g>
            </g>
          </g>
          <g className="ta-s"><path d="M18 40 L30 152 Q60 160 90 152 L102 40"/><ellipse cx="60" cy="40" rx="42" ry="7"/></g>
          <path className="ta-s2" opacity=".6" d="M28 62 L37 142"/>
          <path className="ta-foam" d="M18 40 Q13 23 28 21 Q30 7 46 11 Q58 1 70 11 Q87 7 92 21 Q107 23 102 40 Q60 51 18 40 Z"/>
        </svg>
  );
}

/** One publication, pulled apart into the fields the enrichment extracts. */
export function PulledApart({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-fields ${className ?? ''}`} viewBox="-6 -8 242 216" aria-hidden="true" focusable="false">
        <rect className="ta-sheet" x="126" y="8" width="98" height="184" rx="6"/>
        <path className="ta-s" d="M138 30h74"/><path className="ta-s2" d="M138 42h56"/>
        <path className="ta-s2" d="M138 62h74M138 76h64M138 90h74M138 104h48M138 118h70M138 132h60M138 146h72M138 160h40"/>
        <rect className="ta-s2" x="138" y="170" width="32" height="12" rx="3"/>
        <g className="ta-s2" opacity=".6">
          <path d="M124 40 Q104 32 84 28"/><path d="M124 78 Q104 70 84 64"/>
          <path d="M124 110 Q104 104 84 100"/><path d="M124 140 Q104 138 84 136"/>
          <path d="M124 168 Q104 170 84 172"/>
        </g>
        <rect className="ta-acc" x="6" y="54" width="78" height="20" rx="6"/>
        <g className="ta-s"><rect x="6" y="18" width="78" height="20" rx="6"/><rect x="6" y="90" width="78" height="20" rx="6"/><rect x="6" y="126" width="78" height="20" rx="6"/><rect x="6" y="162" width="78" height="20" rx="6"/></g>
        <g className="ta-s2"><circle cx="19" cy="28" r="2.4"/><circle cx="19" cy="100" r="2.4"/><circle cx="19" cy="136" r="2.4"/><circle cx="19" cy="172" r="2.4"/>
          <path d="M29 28h44M29 100h38M29 136h46M29 172h34"/></g>
        <g className="ta-s2"><circle cx="19" cy="64" r="2.4"/><path d="M29 64h42"/></g>
      </svg>
  );
}

/** A paper plane off the stack it was folded from: the team ships, weekly, out of the documents. */
export function ShipsWeekly({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-ship ${className ?? ''}`} viewBox="-6 -8 252 186" aria-hidden="true" focusable="false">
        <rect className="ta-sheet" x="176" y="146" width="58" height="12" rx="3"/>
        <rect className="ta-sheet" x="182" y="134" width="52" height="12" rx="3"/>
        <rect className="ta-sheet" x="188" y="122" width="46" height="12" rx="3"/>
        <path className="ta-s2" opacity=".6" strokeDasharray="5 6" d="M198 116 C 184 114, 166 110, 138 101"/>
        <path className="ta-acc" d="M26 62 L104 76 L126 106 Z"/>
        <path className="ta-s" d="M26 62 L132 14 L104 76 L126 106 Z"/>
        <path className="ta-s" d="M26 62 L104 76"/>
      </svg>
  );
}

/** The board rises; the price line stays level and runs straight through it. */
export function FlatPrice({ className }: ArtProps) {
  return (
  <svg className={`tenders-art tenders-art-flat ${className ?? ''}`} viewBox="-8 -10 276 202" aria-hidden="true" focusable="false">
        <g>
          <rect className="ta-sheet" x="206" y="136" width="26" height="24" rx="2"/><path className="ta-s2" d="M206 148h26"/>
          <rect className="ta-sheet" x="170" y="124" width="26" height="36" rx="2"/><path className="ta-s2" d="M170 136h26M170 148h26"/>
          <rect className="ta-sheet" x="134" y="100" width="26" height="60" rx="2"/><path className="ta-s2" d="M134 112h26M134 124h26M134 136h26M134 148h26"/>
          <rect className="ta-sheet" x="98" y="76" width="26" height="84" rx="2"/><path className="ta-s2" d="M98 88h26M98 100h26M98 112h26M98 124h26M98 136h26M98 148h26"/>
          <rect className="ta-sheet" x="62" y="52" width="26" height="108" rx="2"/><path className="ta-s2" d="M62 64h26M62 76h26M62 88h26M62 100h26M62 112h26M62 124h26M62 136h26M62 148h26"/>
          <rect className="ta-sheet" x="26" y="28" width="26" height="132" rx="2"/><path className="ta-s2" d="M26 40h26M26 52h26M26 64h26M26 76h26M26 88h26M26 100h26M26 112h26M26 124h26M26 136h26M26 148h26"/>
        </g>
        <path className="ta-s" d="M14 160 H244"/>
        <path className="ta-line" strokeDasharray="5 6" d="M196 88 H10"/>
        <path className="ta-brass" d="M198 76 H244 L256 88 L244 100 H198 Z"/>
        <circle className="ta-s2" cx="240" cy="88" r="2.6"/>
      </svg>
  );
}
